import { NavService } from '@thoughtspot/blink-context';
import { isFreeTrialV2ExperienceEnabled } from '@thoughtspot/feature-service';
import { i18nKeys, translate } from '@thoughtspot/i18n';
import {
    getBlackListedWordsForPassword,
    getPasswordStrategy,
    getUserDisplayName,
    getUserPersona,
    getWhiteListedEmailDomainsForReport,
    hasSeenFreeTrialOnboarding,
    hasSeenOnboarding,
    isEdgeEnabled,
    isEdgeOnboardingCompleted,
    isOnboardingEnabled,
    isSparkFlowEnabled,
    PasswordStrategy,
    UserPersona,
} from '@thoughtspot/session-service';
import {
    getIdentityServicePublicUrl,
    getIdpDomain,
} from '@thoughtspot/system-config-service';
import { zxcvbn } from '@zxcvbn-ts/core';
import { debounce, isEmpty, isEqual, isUndefined } from 'lodash';
import { RefObject } from 'react';
import { isAppInIFrame, isEmbeddedApp } from '/@utils/embed-base.util';
import {
    AccountActivationModel,
    OktaActivationModel,
} from './account-activation/account-activation.container';
import {
    getPasswordRequirements,
    initialError,
    initialValidation,
    setConfirmPasswordError,
    setCreatePasswordError,
} from './account-activation/account-activation.utils';

const BACKEND_SESSION_EXPIRY_CODE = 10003;
const SNOWFLAKE_PARTNER_CONNECT_CONTRACT =
    'https://www.thoughtspot.com/legal/cloud-evaluation-agreement';
const SAAS_PRODUCTION_CONTRACT =
    'https://www.thoughtspot.com/legal/\thoughtspot-cloud-subscription-agreement';
const FREE_TRIAL_CONTRACT =
    'https://www.thoughtspot.com/legal/cloud-evaluation-agreement';

export type ErrorState = 'DEFAULT' | 'ERROR' | 'SUCCESS';
export const VALIDATION_DELAY_IN_MS = 500;

export interface ValidationType {
    id: string;
    text?: string;
    regex: RegExp;
    state: ErrorState;
}

const errorTextPrefix = 'freetrial.validation';
export const CUSTOM_LENGTH_ERROR_TEXT = 'freetrial.validation.customLength';

export const userIdParam = 'id';
export const PASSWORD_STRENGTH_SCORE = 2;
export const INITIAL_PASSWORD_CONFIG = {
    password: false,
    confirmPassword: false,
};
export const TSREF = 'ts_ref';

export const blackListedWords: string[] = [];

interface Validation {
    errorText: string;
    toolTipText: string;
    rules: ValidationType[];
}

/**
 *
 * @param t - translation object where error text can be assigned
 * @returns ValidationTypes
 */
const getValidationObjects = (t: any): ValidationType[] => {
    return [
        {
            id: 'length',
            regex: /(?=.{8,})/,
            state: 'DEFAULT',
            text: t(`${errorTextPrefix}.length`),
        },
        {
            id: 'lower',
            regex: /(?=.*[a-z])/,
            state: 'DEFAULT',
            text: t(`${errorTextPrefix}.lower`),
        },
        {
            id: 'upper',
            regex: /(?=.*[A-Z])/,
            state: 'DEFAULT',
            text: t(`${errorTextPrefix}.upper`),
        },
        {
            id: 'special_character',
            // eslint-disable-next-line no-useless-escape
            regex: /[-!$%^&*()_+|~=`'"\\{}\[\]:\/;<>?,.@#]/,
            state: 'DEFAULT',
            text: t(
                isFreeTrialV2ExperienceEnabled()
                    ? `${errorTextPrefix}.new.special_character`
                    : `${errorTextPrefix}.special_character`,
            ),
        },
        {
            id: 'digit',
            regex: /(?=.*\d)/,
            state: 'DEFAULT',
            text: t(`${errorTextPrefix}.digit`),
        },
    ];
};

export const showOnboarding = (navService: NavService) => {
    // First, if the current cluster and app can show onboarding. The logic here is a sum of
    // 1. The cluster must have onboarding enabled
    // 2. The current URL is not a redirect link to a pinboard or saved answer
    // 3. The current URL is not an embed URL with /embed/ in it
    // 4. The APP is not embedded in an iframe
    // 5. The APP is not serving an embedded use-case
    // Second, if the user is elgible for onboarding
    // 1. User does not have persona OR User has not seen onboarding
    const isRedirectToPinboardOrAnswer =
        // navService.isCurrentPinboardPath() ||
        navService.isCurrentSavedAnswerPath();

    const canShowOnboarding =
        !isRedirectToPinboardOrAnswer &&
        // !navService.isEmbedded() &&
        !isAppInIFrame() &&
        !isEmbeddedApp();

    const isOnboardingPermitted = isOnboardingEnabled() && canShowOnboarding;
    // If onboarding is not permitted, we don't want to show onboarding page
    if (!isOnboardingPermitted) return false;

    if (isSparkFlowEnabled() && isEdgeEnabled()) {
        return !hasSeenOnboarding() && !isEdgeOnboardingCompleted();
    }

    if (isFreeTrialV2ExperienceEnabled()) {
        return (
            !hasSeenOnboarding() &&
            !isEdgeEnabled() &&
            !hasSeenFreeTrialOnboarding()
        );
    }

    // If user has isEdgeEnabled set to true (i.e the new onboarding)
    // and has not seen edgeOnboarding (i.e edgeOnboardingCompleted) is set to false
    if (isEdgeEnabled()) {
        // This is added because for exisiting user before edge onboarding was introduced
        // we should not show edge onboarding again
        return !hasSeenOnboarding() && !isEdgeOnboardingCompleted();
    }

    const isUserPersonaSet = getUserPersona() !== UserPersona.UNDEFINED;
    const isUserEligibleForOnboarding =
        !isUserPersonaSet || !hasSeenOnboarding();

    return isUserEligibleForOnboarding;
};

/**
 *
 * @param t - translation object where error text can be assigned
 * @returns ValidationTypes
 */
const getOktaValidationObjects = (
    t: any,
    length = 8,
    isUpper = true,
    isLower = true,
    isSymbols = true,
    isNumbers = true,
): ValidationType[] => {
    const validations: ValidationType[] = [
        {
            id: 'length',
            // eslint-disable-next-line security/detect-non-literal-regexp
            regex: new RegExp(`(?=.{${length},})`),
            state: 'DEFAULT',
            text: t(CUSTOM_LENGTH_ERROR_TEXT, {
                length,
            }),
        },
    ];
    if (isLower) {
        validations.push({
            id: 'lower',
            regex: /(?=.*[a-z])/,
            state: 'DEFAULT',
            text: t(`${errorTextPrefix}.lower`),
        });
    }
    if (isUpper) {
        validations.push({
            id: 'upper',
            regex: /(?=.*[A-Z])/,
            state: 'DEFAULT',
            text: t(`${errorTextPrefix}.upper`),
        });
    }
    if (isSymbols) {
        validations.push({
            id: 'special_character',
            // eslint-disable-next-line no-useless-escape
            regex: /[-!$%^&*()_+|~=`'"\\{}\[\]:\/;<>?,.@#]/,
            state: 'DEFAULT',
            text: t(`${errorTextPrefix}.special_character`),
        });
    }
    if (isNumbers) {
        validations.push({
            id: 'digit',
            regex: /(?=.*\d)/,
            state: 'DEFAULT',
            text: t(`${errorTextPrefix}.digit`),
        });
    }

    return validations;
};

const getIdentityV2PublicUrl = (): string => {
    const IDENTITY_PUBLIC_URL =
        getIdentityServicePublicUrl() ||
        'https://identity.dataplane-public.thoughtspotdev.cloud/v1';
    return `${IDENTITY_PUBLIC_URL}/public`;
};

const getBaseOktaActivationURL = (clientId: string): string => {
    return `${getIdentityV2PublicUrl()}/${clientId}/activation`;
};

/**
 * get host name from url or empty string
 * Need to move this function to navService
 */
const getOidcLoginUriOrigin = (path: string): string => {
    try {
        const url = new URL(path);
        return url.origin;
    } catch (error) {
        return '';
    }
};

/**
 * Redirect to okta login page
 * Need to move this function to navService
 */
const goToOktaLogin = (issuerLoginUri: string, stateToken: string): void => {
    const host = getOidcLoginUriOrigin(issuerLoginUri);
    /**
     * getOidcLoginUriFromIssuers fn returns the oidc login url from issuers config property
     * getOidcLoginUriOrigin fn returns only host name
     */
    window.location.href = `${host}/login/sessionCookieRedirect?token=${stateToken}&redirectUrl=${host}/signin`;
};

/**
 * Redirect to okta login page
 * Need to move this function to navService
 */
const goToOktaLoginWithClusterRedirect = (
    issuerLoginUri: string,
    stateToken: string,
    clusterUrl: string,
): void => {
    const host = getOidcLoginUriOrigin(issuerLoginUri);
    /**
     * getOidcLoginUriFromIssuers fn returns the oidc login url from issuers config property
     * getOidcLoginUriOrigin fn returns only host name
     */
    window.location.href = `${host}/login/sessionCookieRedirect?token=${stateToken}&redirectUrl=${clusterUrl}`;
};

const getPlaceholder = (translationKey: i18nKeys): string =>
    isSparkFlowEnabled() ? '' : translate(translationKey);

const getEmailLabel = (
    isOnlyForOkta: boolean,
    isResetPasswordActiveForSWCluster: boolean,
    isNonOktaAndNonFreeTrialActivation: boolean,
) => {
    if (isOnlyForOkta || isResetPasswordActiveForSWCluster) {
        return translate('accountActivation.username');
    }
    if (isNonOktaAndNonFreeTrialActivation || isSparkFlowEnabled()) {
        return translate('accountActivation.email');
    }
    return translate('freetrial.field.email');
};

const isEulaServiceRequired = (
    clusterId: string,
    hasAdminPrivileges: boolean,
    DEV_MODE: string,
): boolean => {
    if (!clusterId || clusterId === DEV_MODE) return false;
    return hasAdminPrivileges;
};

const updateBlackListedWordsForPassword = (
    modelRef: RefObject<AccountActivationModel>,
) => {
    const { username, email } = modelRef.current;
    if (username) blackListedWords.push(username.toLowerCase());
    if (email) blackListedWords.push(email.toLowerCase());
    const userDisplayName = getUserDisplayName()
        .replace(/\s+/g, ' ')
        .trim();
    if (userDisplayName) {
        const names = userDisplayName.split(' ');
        names.forEach((name: string) => {
            blackListedWords.push(name.toLowerCase());
        });
    }
    const configBlackListedWords = getBlackListedWordsForPassword();
    if (isEmpty(configBlackListedWords)) {
        configBlackListedWords.forEach((word: string) => {
            blackListedWords.push(word.toLowerCase());
        });
    }
};

/** method to validate the create password inputs with list of regex */
const checkValidations = (
    validation: Validation,
    modelRef:
        | RefObject<AccountActivationModel>
        | { current: AccountActivationModel },
    hasError: any,
    setHasError: (arg0: any) => void,
) => {
    let isValid = true;
    const tmpValidations = validation.rules.map((rule: any) => {
        // eslint-disable-next-line security/detect-non-literal-regexp
        if (!new RegExp(rule.regex).test(modelRef.current.newPassword)) {
            // eslint-disable-next-line no-param-reassign
            rule.state = 'ERROR';
            isValid = false;
        } else {
            // eslint-disable-next-line no-param-reassign
            rule.state = 'SUCCESS';
        }
        return rule;
    });
    if (!isValid) {
        setCreatePasswordError(true, hasError, setHasError);
    } else {
        setCreatePasswordError(false, hasError, setHasError);
    }
    return tmpValidations;
};

const checkForWhitelistedEmail = (email: string): boolean => {
    let isWhitelistedDomainEmail = true;
    const validEmailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const whitelistedDomainList = getWhiteListedEmailDomainsForReport();
    const emailDomain = email.split('@')[1];
    const ignoreEmailValidation =
        whitelistedDomainList.length === 1 && whitelistedDomainList[0] === '*';
    if (
        !validEmailRegex.test(email) ||
        (!ignoreEmailValidation &&
            (emailDomain === email ||
                !whitelistedDomainList.includes(emailDomain)))
    ) {
        isWhitelistedDomainEmail = false;
    }
    return isWhitelistedDomainEmail;
};
const passwordStrategy = (isOnlyForOkta: boolean): PasswordStrategy =>
    isOnlyForOkta ? PasswordStrategy.GUESS_ESTIMATE : getPasswordStrategy();

const comparePasswords = (
    rules: any,
    setValidation: any,
    hasError: any,
    setHasError: any,
    isOnlyForOkta: boolean,
) => {
    setValidation({
        errorText: translate('freetrial.validation.passwordnotmatch'),
        toolTipText: getPasswordRequirements(passwordStrategy(isOnlyForOkta)),
        rules,
    });
    setConfirmPasswordError(true, hasError, setHasError);
};

const checkPasswords = (
    validation: Validation,
    modelRef: RefObject<AccountActivationModel>,
    hasError: any,
    setHasError: any,
    setValidation: any,
    isOnlyForOkta: boolean,
    oktaInitialValidationRules: ValidationType[],
): boolean => {
    const { newPassword, confirmPassword } = modelRef.current;
    const { rules } = validation;

    /** To Reset the error text whenever we start typing in
     *  create password field  */
    setValidation({
        errorText: '',
        toolTipText: '',
        rules,
    });
    if (
        isEmpty(newPassword) &&
        (isEmpty(confirmPassword) || isUndefined(confirmPassword))
    ) {
        if (isOnlyForOkta) {
            setValidation({
                errorText: '',
                toolTipText: '',
                rules: [...oktaInitialValidationRules],
            });
        } else {
            setValidation(initialValidation());
        }
        setHasError(initialError());
        return false;
    }
    if (isUndefined(confirmPassword)) {
        if (
            !isEqual(newPassword, confirmPassword) &&
            !isEmpty(confirmPassword)
        ) {
            comparePasswords(
                rules,
                setValidation,
                hasError,
                setHasError,
                isOnlyForOkta,
            );
            return false;
        }
    }
    if (isEqual(newPassword, confirmPassword)) {
        setValidation({
            errorText: '',
            toolTipText: '',
            rules: isOnlyForOkta
                ? oktaInitialValidationRules
                : validation.rules,
        });
        checkValidations(validation, modelRef, hasError, setHasError);
        setConfirmPasswordError(false, hasError, setHasError);
        return true;
    }
    if (!isEmpty(newPassword) && isEmpty(confirmPassword)) {
        setHasError(initialError());
        return true;
    }
    return true;
};

const isValidatePasswordStrength = (
    value: string,
    isOnlyForOkta: boolean,
    guessEtimateEvent: RefObject<{ guess_estimate_attempt: number }>,
) => {
    let result = { errorText: '', toolTipText: '' };
    if (passwordStrategy(isOnlyForOkta) === PasswordStrategy.GUESS_ESTIMATE) {
        const { score } = zxcvbn(value);
        if (score < PASSWORD_STRENGTH_SCORE) {
            // eslint-disable-next-line no-param-reassign
            guessEtimateEvent.current.guess_estimate_attempt++;
        }
        const errorText: string =
            score < PASSWORD_STRENGTH_SCORE
                ? translate('freetrial.validation.strongpassword')
                : '';
        const toolTipText = !isEmpty(errorText)
            ? translate('freetrial.passwordrequirements')
            : '';
        result = { errorText, toolTipText };
    }
    return result;
};

const isPasswordContainsBlackListedWords = (
    value: string,
    tmpValidations: any[],
    isOnlyForOkta: boolean,
    setFormValid: any,
    hasError: any,
    setValidation: any,
) => {
    if (isOnlyForOkta) {
        return false;
    }
    let isPasswordContainsBlackListedWord = true;
    let result = { errorText: '', toolTipText: '' };
    const pwd = value.toLowerCase();
    if (blackListedWords.some(word => pwd.includes(word))) {
        const errorText: string = translate('msg_code.12914.summary');
        result = { ...result, errorText };
    }
    const { errorText, toolTipText } = result;
    if (isEmpty(errorText)) {
        setFormValid(false);
        isPasswordContainsBlackListedWord = false;
    }
    if (hasError.confirm.error) {
        return true;
    }
    setValidation({
        errorText,
        toolTipText,
        rules: tmpValidations,
    });
    return isPasswordContainsBlackListedWord;
};

const isPasswordContainsUsername = (
    value: string,
    tmpValidations: any[],
    oktaRef: RefObject<OktaActivationModel>,
    setFormValid: any,
    hasError: any,
    setValidation: any,
) => {
    let isPwdContainsUsername = true;
    let result = { errorText: '', toolTipText: '' };
    const pwd = value.toLowerCase();
    const userName = oktaRef.current.username.toLowerCase();
    const idpDomainName = getIdpDomain();
    if (pwd.indexOf(userName) !== -1) {
        const errorText: string = translate(
            'freetrial.validation.containspassword',
        );
        result = { ...result, errorText };
    } else if (pwd.includes(idpDomainName)) {
        const errorText: string = translate(
            'freetrial.validation.containsclustername',
        );
        result = { ...result, errorText };
    }
    const errorText: string = result.errorText;
    const toolTipText: string = result.toolTipText;
    if (isEmpty(errorText)) {
        setFormValid(false);
        isPwdContainsUsername = false;
    }
    if (hasError.confirm.error) {
        return true;
    }
    setValidation({
        errorText,
        toolTipText,
        rules: tmpValidations,
    });
    return isPwdContainsUsername;
};

/** method to delay the error message to be shown
 * when there is score check happening on the UI
 */
const delayScoreValidation = (
    modelRef: RefObject<AccountActivationModel>,
    setFormValid: any,
    validation: Validation,
    hasError: any,
    setHasError: any,
    isOnlyForOkta: boolean,
    oktaRef: RefObject<OktaActivationModel>,
    setValidation: any,
    guessEtimateEvent: RefObject<{ guess_estimate_attempt: number }>,
) => {
    const value = modelRef.current.newPassword;
    setFormValid(true);
    const tmpValidations = checkValidations(
        validation,
        modelRef,
        hasError,
        setHasError,
    );
    const isAllValid = tmpValidations.every(rule => rule.state === 'SUCCESS');
    if (isOnlyForOkta && isAllValid) {
        const isPwdContainsUsername = isPasswordContainsUsername(
            value,
            tmpValidations,
            oktaRef,
            setFormValid,
            hasError,
            setValidation,
        );
        if (isPwdContainsUsername) return;
    }
    if (!isAllValid) return;
    if (
        isAllValid &&
        isPasswordContainsBlackListedWords(
            value,
            tmpValidations,
            isOnlyForOkta,
            setFormValid,
            hasError,
            setValidation,
        )
    ) {
        return;
    }
    debounce(() => {
        const { errorText, toolTipText } = isValidatePasswordStrength(
            value,
            isOnlyForOkta,
            guessEtimateEvent,
        );
        if (isEmpty(errorText)) {
            setFormValid(false);
        }
        if (hasError.confirm.error) {
            return;
        }
        setValidation({
            errorText,
            toolTipText,
            rules: tmpValidations,
        });
    }, VALIDATION_DELAY_IN_MS)();
};

export {
    BACKEND_SESSION_EXPIRY_CODE,
    SNOWFLAKE_PARTNER_CONNECT_CONTRACT,
    SAAS_PRODUCTION_CONTRACT,
    FREE_TRIAL_CONTRACT,
    getBaseOktaActivationURL,
    getOktaValidationObjects,
    getValidationObjects,
    goToOktaLogin,
    goToOktaLoginWithClusterRedirect,
    getEmailLabel,
    getPlaceholder,
    isEulaServiceRequired,
    checkValidations,
    checkForWhitelistedEmail,
    passwordStrategy,
    checkPasswords,
    comparePasswords,
    delayScoreValidation,
    updateBlackListedWordsForPassword,
};
